import { type PropertyDto, type TenantDto } from '../../services/api';
import { t } from '../../services/i18n';
import './rc-candidate-column';
import { RcCandidateColumn } from './rc-candidate-column';
import {
  EMPTY_CANDIDATE_ICON,
  EMPTY_PROPERTY_ICON,
  esc,
  getCandidateStatuses,
  isExpiredConverted,
  PLUS_ICON,
  PROPERTY_BANNER_ICON,
  PROPERTY_TAB_ICON,
} from './candidate-shared';

export class RcCandidateBoardView extends HTMLElement {
  candidates: TenantDto[] = [];
  properties: PropertyDto[] = [];
  activePropertyId: string | null = null;

  render(): void {
    const statuses = getCandidateStatuses();

    if (this.properties.length === 0) {
      this.innerHTML = `
        <div class="page-header">
          <h1>${t('waitinglist.title')}</h1>
        </div>
        <div class="empty-state card waitinglist-empty-state waitinglist-empty-state--properties">
          ${EMPTY_PROPERTY_ICON}
          <h3>${t('waitinglist.noPropertiesTitle')}</h3>
          <p>${t('waitinglist.noPropertiesBody')}</p>
          <a href="/properties/" class="btn btn-primary">${t('property.add')}</a>
        </div>
      `;
      return;
    }

    const singleProperty = this.properties.length === 1;
    const activeProperty = this.properties.find((property) => property.id === this.activePropertyId) ?? this.properties[0];
    const activeCandidates = this.candidates.filter((candidate) => candidate.propertyId === activeProperty.id);
    const activeCount = activeCandidates.filter((candidate) => !isExpiredConverted(candidate)).length;

    this.innerHTML = `
      <div class="wl-header">
        <div>
          <h1 class="wl-title">${t('waitinglist.title')}</h1>
          <p class="wl-subtitle">${t('waitinglist.subtitle')}</p>
        </div>
        <button class="btn btn-primary" data-action="add">
          ${PLUS_ICON}
          ${t('waitinglist.addButton')}
        </button>
      </div>

      ${!singleProperty ? `
        <div class="property-nav" role="tablist">
          ${this.properties.map((property) => {
            const count = this.candidates
              .filter((candidate) => candidate.propertyId === property.id)
              .filter((candidate) => !isExpiredConverted(candidate)).length;
            const isActive = property.id === activeProperty.id;
            return `
              <button class="property-tab${isActive ? ' active' : ''}" data-action="select-property" data-property-id="${property.id}" role="tab" aria-selected="${isActive}">
                ${PROPERTY_TAB_ICON}
                ${esc(property.displayName || property.address)}
                ${count > 0 ? `<span class="tab-count">${count}</span>` : ''}
              </button>`;
          }).join('')}
        </div>` : ''}

      <div class="property-banner">
        <div class="property-banner-info">
          <div class="property-banner-icon">${PROPERTY_BANNER_ICON}</div>
          <div>
            <div class="property-banner-name">${esc(activeProperty.displayName || activeProperty.address)}</div>
            <div class="property-banner-meta">${t('waitinglist.pipelineCount', { count: activeCount })}</div>
          </div>
        </div>
        <div class="wl-stats">
          ${statuses.map((status) => {
            const count = activeCandidates.filter((candidate) => candidate.status === status.key && !isExpiredConverted(candidate)).length;
            return `
              <div class="wl-stat">
                <div class="wl-stat-value">${count}</div>
                <div class="wl-stat-label">${status.label}</div>
              </div>`;
          }).join('')}
        </div>
      </div>

      ${activeCandidates.length === 0
        ? `<div class="empty-state card waitinglist-empty-state">
            ${EMPTY_CANDIDATE_ICON}
            <h3>${t('waitinglist.emptyTitle')}</h3>
            <p>${t('waitinglist.emptyBody')}</p>
            <button class="btn btn-primary" data-action="add">${t('waitinglist.addFirst')}</button>
          </div>`
        : `<div class="kanban-board">
            ${statuses.map(() => '<rc-candidate-column></rc-candidate-column>').join('')}
          </div>`
      }
    `;

    this.querySelectorAll<HTMLElement>('[data-action="add"]').forEach((button) => {
      button.addEventListener('click', () => this.dispatchEvent(new CustomEvent('candidate-add', { bubbles: true })));
    });

    this.querySelectorAll<HTMLElement>('[data-action="select-property"]').forEach((button) => {
      button.addEventListener('click', () => this.dispatchEvent(new CustomEvent('candidate-select-property', {
        bubbles: true,
        detail: { propertyId: button.dataset.propertyId },
      })));
    });

    const columns = this.querySelectorAll<RcCandidateColumn>('rc-candidate-column');
    columns.forEach((column, index) => {
      column.status = statuses[index];
      column.candidates = activeCandidates;
      column.render();
    });
  }
}

if (!customElements.get('rc-candidate-board-view')) {
  customElements.define('rc-candidate-board-view', RcCandidateBoardView);
}
